import { Location } from "@angular/common";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { NbDialogService, NbToastrService } from "@nebular/theme";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { JsonEditorOptions } from "ang-jsoneditor";
import { saveAs } from "file-saver";
import * as JSZip from "jszip";
import { Subscription, asapScheduler, scheduled } from "rxjs";
import { filter, switchMap, withLatestFrom } from "rxjs/operators";
import { defaultWebsdkConfig } from "../../../environments/common";
import { environment } from "../../../environments/environment";
import {
  addTagToAppSuccess,
  assignTags,
  createApp,
  createAppSuccess,
  deleteApp,
  deleteTagFromAppSuccess,
  updateApp,
  updateAppSuccess,
} from "../../app.actions";
import { App, TagChanges } from "../../app.models";
import {
  selectApiEndpoint,
  selectBaseEndpoint,
  selectRouteParams,
  selectUrl,
} from "../../app.selector";
import { TestPushComponent } from "../test-push/test-push.component";

declare var Pace: any;

@Component({
  selector: "app-app-detail",
  templateUrl: "./app-detail.component.html",
  styleUrls: ["./app-detail.component.scss"],
})
export class AppDetailComponent implements OnInit, OnDestroy {
  @ViewChild("editorInstance") editorInstance;
  @ViewChild("deleteDialog") deleteDialog: TemplateRef<any>;
  app: App = {} as App;
  platformConfigs: any;
  app$: any;
  endpoint: string;
  icon: File;
  updates$: Subscription;
  isNew: boolean;
  env = environment;
  tagChanges: TagChanges[] = [];
  selectedTags: number[];
  uploads: { [key: string]: File } = {};
  editorOptions: JsonEditorOptions;
  pushTemplate: any[] = [];
  twitterSigninRequested: boolean;
  twitterSigninRequestedOA2: boolean;
  previewWindow: Window;
  websdkConfig: any;
  websdkCss = {
    "--pd-logo-url": "logo.svg",
    "--pd-bell-url": "bell.svg",
    "--pd-font": "arial",
    "--pd-background": "#f5f5f5",
    "--pd-background-head": "#f5f5f5",
    "--pd-color-primary": "#1781e3",
    "--pd-bell-left": "30px",
  };
  requiredInputFields: NodeListOf<HTMLObjectElement>;
  invalidInputFields: HTMLObjectElement[] = [];
  requiredDropzone: NodeListOf<HTMLObjectElement>;
  invalidDropzone: HTMLObjectElement[] = [];
  challenge: string;

  constructor(
    private http: HttpClient,
    private store: Store,
    private updates: Actions,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private toastr: NbToastrService,
    private dialogService: NbDialogService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
    public location: Location
  ) {
    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.mode = "code"; //set only one mode
    this.editorOptions.mainMenuBar = false;
    this.editorOptions.statusBar = false;
    this.challenge = "pushchallenge";
  }

  get defaultPayload() {
    return this.app.parameters.defaultPayload === "true";
  }
  set defaultPayload(val) {
    this.app.parameters.defaultPayload = val ? "true" : "false";
  }

  ngOnInit(): void {
    this.platformConfigs = environment.platformconfigs;
    // reload after update
    this.updates$ = this.updates
      .pipe(
        ofType(
          updateAppSuccess,
          createAppSuccess,
          addTagToAppSuccess,
          deleteTagFromAppSuccess
        ),
        withLatestFrom(this.store.select(selectRouteParams))
      )
      .subscribe(([action, params]) => {
        this.title.setTitle(action.payload.app.name);
        this.app = JSON.parse(JSON.stringify(action.payload.app));
        this.selectedTags = [
          ...action.payload.app.tags,
          ...action.payload.app.groups,
        ].map((e) => e.id);

        if (
          action.type === createAppSuccess.type ||
          action.type === updateAppSuccess.type
        ) {
          this.store.dispatch(
            assignTags({
              payload: { app: action.payload.app, changes: this.tagChanges },
            })
          );
          this.tagChanges = [];
        }

        if (params.id === "new") {
          this.isNew = false;
          this.router.navigate([
            `/${params.clientId}/apps/${action.payload.app.id}`,
          ]);
        }

        if (this.twitterSigninRequested) {
          this.twitterSigninRequested = false;
          const callback = encodeURIComponent(
            `${location.origin}?route=${params.clientId}/apps/${action.payload.app.id}&provider=twitter`
          );
          this.http
            .get(
              `${this.env.awsApiGateway}/twitter-signin/signin?callback=${callback}`
            )
            .subscribe(
              (resp: any) =>
                (document.location.href = `https://twitter.com/oauth/authorize?oauth_token=${resp.oauthToken}`)
            );
        }

        if (this.twitterSigninRequestedOA2) {
          this.twitterSigninRequestedOA2 = false;
          const client_id = this.app.parameters.twitterConsumerKey;
          const redirect_uri = location.origin; //callback
          const state = `${params.clientId}/apps/${action.payload.app.id}`; //state takes callback route

          const authUrl =
            "https://twitter.com/i/oauth2/authorize?response_type=code&client_id=" +
            client_id +
            "&redirect_uri=" +
            redirect_uri +
            "&scope=tweet.read%20users.read%20tweet.write%20offline.access&state=" +
            state +
            " &code_challenge=" +
            this.challenge +
            "&code_challenge_method=plain";
          document.location.href = authUrl;
        }
      });

    this.store
      .select(selectBaseEndpoint)
      .pipe(filter((e) => !!e))
      .subscribe((e) => (this.endpoint = e));

    // fetch app with given url params as soon as endpoint is there (contract is choosed)
    this.app$ = this.store
      .select(selectApiEndpoint)
      .pipe(
        filter((c) => !!c),
        withLatestFrom(
          this.store.select(selectUrl),
          this.store.select(selectRouteParams)
        ),
        switchMap(([endpoint, url, params]) => {
          if (params.template) {
            const tpl = this.platformConfigs[params.template].appTemplate;
            return scheduled([{ ...tpl }], asapScheduler);
          }
          return this.http.get(`${endpoint}${url}`);
        })
      )
      .subscribe(
        (app: App) => {
          // Clears the JSON string
          try {
            this.pushTemplate = JSON.parse(
              app.parameters.pushTemplate
                .toString()
                .replace(/(\r\n|\n|\r|\s)/gm, "")
                .replace(/[\u0000|\u001F|\u007F|\u009F|\u200B]/g, "")
            );
          } catch (error) {
            this.toastr.danger(
              this.translate.instant("error_invalid_payload_json")
            );
            app.parameters.pushTemplate = "";
          }

          try {
            this.websdkConfig = JSON.parse(
              app.parameters.websdkConfig.toString()
            );
            this.websdkCss = JSON.parse(app.parameters.websdkCss.toString());
          } catch (error) {
            this.websdkConfig = defaultWebsdkConfig;
          }

          try {
            this.websdkCss = JSON.parse(app.parameters.websdkCss.toString());
          } catch (error) {
            this.websdkConfig = defaultWebsdkConfig;
          }

          this.isNew = !app.id;
          this.title.setTitle(`${this.isNew ? "new " : ""}${app.name}`);
          this.app = { ...app };
          this.getTwitterCredentials();
          this.getTwitterCredentialsOA2();
          this.selectedTags = [...this.app.tags, ...this.app.groups].map(
            (e) => e.id
          );
        },
        (e: HttpErrorResponse) =>
          this.router.navigate(["/404"], {
            state: { msg: "The app doesn't exist." },
          })
      );
  }

  save() {
    this.getInvalidInputFields();
    this.getInvalidDropzone();
    const invalidInputFieldsNames = this.invalidInputFields.map(
      (field) => field.parentElement.children[0].textContent
    );
    const invalidDropzoneNames = this.invalidDropzone.map(
      (field) => field.parentElement.children[0].textContent
    );
    if ((this.invalidInputFields.length | this.invalidDropzone.length) !== 0) {
      if (this.invalidInputFields.length !== 0) {
        this.toastr.danger(
          invalidInputFieldsNames.join(", "),
          this.translate.instant("missing inputs"),
          { duration: 5000 }
        );
      }
      if (this.invalidDropzone.length !== 0) {
        this.toastr.danger(
          invalidDropzoneNames.join(", "),
          this.translate.instant("missing files"),
          { duration: 5000 }
        );
      }
      this.toastr.danger(this.translate.instant("required missing"));
      return;
    }
    const app = { ...this.app };
    const parameters = { ...this.app.parameters };
    parameters.pushTemplate = JSON.stringify(this.pushTemplate);
    parameters.websdkConfig = JSON.stringify(this.websdkConfig);
    parameters.websdkCss = JSON.stringify(this.websdkCss);

    // parameters.websdkConfig = null;

    app.parameters = parameters;

    const files = Object.entries(this.uploads).map(([key, binary]) => ({
      key,
      binary,
    }));

    if (this.isNew) {
      this.store.dispatch(
        createApp({ payload: { app: app, icon: this.icon, files } })
      );
    } else {
      this.store.dispatch(
        updateApp({ payload: { app: app, icon: this.icon, files } })
      );
    }
    this.uploads = {};
  }

  composeWebSDKConfig() {
    return {
      ...this.websdkConfig,
      app: {
        ...this.websdkConfig.app,
        api: {
          ...this.websdkConfig.app.api,
          id: this.app.id,
          platformId: this.app.platformId,
          baseUrl: this.endpoint + "/push-api/",
          accessToken: this.app.apiUser.accessToken,
        },
        firebase: {
          ...this.websdkConfig.app.firebase,
          projectId: this.app.parameters.firebaseProjectId,
          apiKey: this.app.parameters.firebaseApiKey,
          appId: this.app.parameters.firebaseAppId,
          messageSenderId: this.app.parameters.firebaseMessagingSenderId,
        },
      },
    };
  }

  async websdkPackage() {
    const stage = this.env.production ? "prod" : "stage";
    const zip = new JSZip();
    const files = [
      "webpush.js",
      "webpush-core.js",
      "webpush-core.js.LICENSE.txt",
      "webpush-ui.js",
      "webpush-ui.js.LICENSE.txt",
      "wheel.svg",
      "bell.svg",
      "logo.svg",
      "service-worker.js",
      "service-worker.js.LICENSE.txt",
      "styles.css",
      "unblock-FF-french.gif",
      "unblock-FF-italian.gif",
      "unblock-chrome-german.gif",
      "unblock-FF-german.gif",
      "unblock-chrome-french.gif",
      "unblock-chrome-italian.gif",
      "unblock-chrome-spanish.gif",
      "unblock-FF-spanish.gif",
    ];

    for (const file of files) {
      Pace.start();
      const resp = await fetch(`/websdk/${stage}/${file}`);
      if (resp.status !== 200) {
        return;
      }
      let content;
      if (file.endsWith(".gif")) {
        content = await resp.blob();
      } else {
        content = await resp.text();
      }
      zip.file(`webpush/${file}`, content);
      Pace.stop();
    }
    zip.file(`webpush/config.json`, JSON.stringify(this.composeWebSDKConfig()));
    zip.file(`webpush/webpush-vars.css`, this.websdkCssFile());

    const zipFile = await zip.generateAsync({ type: "blob" });
    saveAs(zipFile, `webpush.zip`);
  }

  websdkCssFile() {
    let cssVars = "";
    for (const key in this.websdkCss) {
      if (key.endsWith("-url")) {
        cssVars = cssVars + `${key}: url(${this.websdkCss[key]});`;
      } else {
        cssVars = cssVars + `${key}: ${this.websdkCss[key]};`;
      }
    }
    return `:root { ${cssVars} }`;
  }

  websdkPreview() {
    const stage = this.env.production ? "prod" : "stage";
    const css = encodeURIComponent(this.websdkCssFile());
    const cfg = encodeURIComponent(JSON.stringify(this.composeWebSDKConfig()));
    if (this.previewWindow) {
      this.previewWindow.close();
    }

    const url =
      location.hostname === "localhost"
        ? `http://localhost:9000/index.html?cfg=${cfg}&css=${css}`
        : `https://push.delivery.ethinking.de/websdk/${stage}/index.html?cfg=${cfg}&css=${css}`;
    this.previewWindow = window.open(url);
  }

  handleClickDelete() {
    this.cd.detectChanges();
    const context = {
      app: this.app,
      delete: () => {
        this.router.navigate([".."], { relativeTo: this.route });
        this.store.dispatch(deleteApp({ payload: this.app }));
      },
    };
    // https://github.com/swimlane/ngx-datatable/issues/721#issuecomment-325550591
    setTimeout(() => {
      this.dialogService.open(this.deleteDialog, { context });
    });
  }

  onFileChange(event, key) {
    this.uploads[key] = event.addedFiles[0];
    if (event.source.__ngContext__[13][0].classList.contains("warning")) {
      event.source.__ngContext__[13][0].classList.remove("warning");
    }
  }

  deleteFile(key, delKey) {
    this.app = JSON.parse(JSON.stringify(this.app));

    this.app.parameterInfos[key] = null;
    delete this.uploads[key];
    this.app.parameters[delKey] = "true";
  }

  testpush() {
    this.dialogService.open(TestPushComponent, {
      context: {
        contextApp: this.app,
      },
    });
  }

  twitterLogin() {
    this.twitterSigninRequested = true;
    this.save();
  }

  twitterLoginOA2() {
    this.twitterSigninRequestedOA2 = true;
    this.save();
  }

  getTwitterCredentials() {
    if (history.state.provider === "twitter") {
      this.http
        .get(
          `${this.env.awsApiGateway}/twitter-signin/callback?oauth_token=${history.state.oauth_token}&oauth_verifier=${history.state.oauth_verifier}`
        )
        .subscribe((cred: any) => {
          this.app.parameters.twitterAccessToken = cred.oauthAccessToken;
          this.app.parameters.twitterAccessTokenSecret =
            cred.oauthAccessTokenSecret;
          this.save();
        });
    }
  }

  async getTwitterCredentialsOA2() {
    if (history.state.code) {
      this.http
        .get(
          `${this.env.awsApiGateway}/twitter-signin/oauth2/callback?code=${history.state.code}&client_id=${this.app.parameters.twitterConsumerKey}&code_verifier=${this.challenge}&redirect_uri=${location.origin}`,
          { observe: "response" }
        )
        .subscribe((res: any) => {
          if (res.ok) {
            (this.app.parameters.twitterAccessToken =
              res.body.twitterAccessToken),
              (this.app.parameters.twitterRefreshToken =
                res.body.twitterRefreshToken);
            this.save();
            return;
          }
          this.toastr.danger(this.translate.instant("unknown_error"));
          return;
        });
    }
  }

  ngOnDestroy() {
    if (this.previewWindow) {
      this.previewWindow.close();
    }
    this.app$.unsubscribe();
    this.updates$.unsubscribe();
  }

  getInvalidInputFields() {
    this.requiredInputFields = document.querySelectorAll(`input[required]`);
    this.invalidInputFields = [];
    this.requiredInputFields &&
      this.requiredInputFields.forEach((item) => {
        if (item.classList.contains("status-danger")) {
          item.classList.remove("status-danger");
          item.classList.add("status-basic");
        }
        if (!item.checkValidity()) {
          item.classList.remove("status-basic");
          item.classList.add("status-danger");
          this.invalidInputFields.push(item);
        }
      });
  }
  getInvalidDropzone() {
    this.requiredDropzone = document.querySelectorAll(`ngx-dropzone.required`);
    this.invalidDropzone = [];
    this.requiredDropzone &&
      this.requiredDropzone.forEach((item) => {
        if (item.classList.contains("warning")) {
          item.classList.remove("warning");
        }
        if (item.childNodes[1].nodeName === "NGX-DROPZONE-LABEL") {
          this.invalidDropzone.push(item);
        }
      });
    this.invalidDropzone.forEach((dropZone) => {
      dropZone.classList.add("warning");
    });
  }
}
